import ProductReviews from './ProductReviews';
import QuickAdd from './QuickAdd';
import AdMindClick from '../AdMind/click';
import { buildVideoElement } from '../ProductCardVideo';
import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';
import { generateVideoObserverCallback, generateVideoObserverOptions } from '../ProductCardVideo/utils';
import { isProductType, imageErrorHandler } from './Utils';
import { isVideo } from '../../utils/video';
import ProductCardMediaGallery from './ProductCardMediaGallery';
import { trackSearchResultClick, trackBrowseResultClick, getConstructorPrice } from '../../utils/ConstructorTracking';
import { buildSource, buildSrcSet, imageBreakpoints, imageSizes } from '../../utils/productCardImages';
import formatPriceWithSymbol from '../../utils/formatPriceWithSymbol';

const currency_symbol = window.theme.currency_symbol ? window.theme.currency_symbol : '$';
const currency_iso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';

export class ProductCard {
  static currencySymbol = currency_symbol;
  static currencyIso = currency_iso;

  // Responsive swatch configuration
  static MOBILE_BREAKPOINT = 768;
  static TABLET_BREAKPOINT = 990;
  static MOBILE_SWATCH_LIMIT = 4;
  static DESKTOP_SWATCH_LIMIT = 5;

  // Image extension white-list
  static IMAGE_EXTENSIONS = ['.jpg', '.webp'];

  /**
   *
   * @param {Object} builder - This will come from a builder
   */
  constructor(builder) {
    this.productCardData = {
      productId: builder.productId || null,
      title: builder.title || null,
      productUrl: builder.productUrl || null,
      productType: builder.productType || null,
      imageUrl: builder.imageUrl || '',
      media: builder.media || [],
      secondImageUrl: builder.secondImageUrl || '',
      imageAlt: builder.title || null,
      price: builder.price || null,
      compareAtPrice: builder.compareAtPrice || null,
      maxCompareAtPrice: builder.maxCompareAtPrice || null,
      tags: builder.tags || null,
      availableVariants: builder.availableVariants || null,
      variants: builder.variants || null,
      options: builder.options || null,
      isRec: builder.isRec || null,
      shouldReportClickToAdmind: builder.shouldReportClickToAdmind || null,
      selectedColor: builder.selectedColor || null,
      recToken: builder.recToken || null,
      index: builder.index || null,
      reviews: builder.reviews || null,
      handle: builder.handle || null,
      promoCreative: builder.promoCreative || null,
      trackingId: builder.trackingId || null,
      sectionId: builder.sectionId || null,
      variantId: builder.variantId || null,
      isCardImageHidden: builder.isCardImageHidden || false,
      badge: builder.badge || null,
      isBadgeVisible: builder.isBadgeVisible || false,
      videoSource: builder.videoSource || null,
      quickAdd: builder.quickAdd || false,
      isCardImageGallery: builder.isCardImageGallery || false,
      promoMessage: builder.promoMessage || '',
      mediaSwitch: builder.mediaSwitch || false,
      podId: builder.podId || null,
      colorSwatches: builder.colorSwatches || false,
      isPlaceholder: builder.isPlaceholder || false,
      location: builder.location || '',
      imageLoading: builder.imageLoading || null,
      imageFetchPriority: builder.imageFetchPriority || null,
    };
    this.extraBaseClassName = builder.baseClassName || '';
    this.productCardNode = builder.productCardNode || undefined;
    this.productCardBadgeNode = undefined;
    this.productCardImageNode = undefined;
    this.productCardImageLinkNode = undefined;
    this.productCardTitleLinkNode = undefined;
    this.productCardQuickViewLinkNode = undefined;
    this.productCardFooterPodNode = undefined;
    this.productCardPriceNode = undefined;
    this.productCardMediaNode = undefined;
    this.productCardMediaContainerNode = undefined;
    this.quickAddInstance = null;
    this.productCardMediaGalleryInstance = null;

    this.init();
  }

  /**
   * Initialize the Product Card instance
   * @returns {void} there is no return
   */
  init() {
    if (!this.productCardNode) return;

    // Create a whole media container for images, videos and QuickAdd (for positioning context)
    this.#setProductMediaContainer();

    // Create either product card image wrapper, video wrapper, or hidden image wrapper
    if (this.productCardData.isCardImageHidden) {
      this.#setProductCardImageHidden();
    } else if (this.productCardData.isCardImageGallery && window.innerWidth < ProductCard.TABLET_BREAKPOINT) {
      this.#setProductCardMediaGallery();
    } else {
      this.#setProductCardMedia();
    }

    this.productCardMediaContainerNode.appendChild(this.productCardMediaNode);

    // Create Product Card brief information Part 1 (Color Swatches)
    this.#setColorSwatches();

    // Create Quick View button, title, badges, built-in Yotpo reviews, and price
    // These aren't user-visible tasks or they don't represent a big impact in the elemnent. So, we can defer it
    setTimeout(() => {
      this.#setTitle();
      this.#setFooter();
      this.#setPrice();
      this.#setBadge();
      if (
        this.productCardData.quickAdd &&
        this.productCardData.variants.length &&
        !isProductType(this.productCardData.productType, 'givex')
      ) {
        this.#setQuickAdd();
      }
      if (isProductType(this.productCardData.productType, 'givex')) {
        this.#setBlankBox();
      }
      // Bind default desktop events like hover and video observers for videos in first slot, it will be ignored in mobile devices
      this.#bindProductCardEvents();
      // this.setQuickViewButton();
      if (this.productCardData.promoMessage) {
        this.#setPromoMessage();
      }
      this.#setReviews();
      // Set initial visibility and setup resize observer
      this.#updateSwatchVisibility();
      this.#setupSwatchResizeObserver();
      this.#bindTrackingEvents();
    }, 0);
  }

  /**
   * Set a whole media container for images, videos and QuickAdd (for positioning context)
   */
  #setProductMediaContainer() {
    // Create container wrapper for images and QuickAdd (for positioning context)
    this.productCardMediaContainerNode = document.createElement('div');
    this.productCardMediaContainerNode.classList.add('product-card-c-basis__media-container');
    // Set extra base class if provided
    this.extraBaseClassName &&
      this.productCardMediaContainerNode.classList.add(`${this.extraBaseClassName}__product-card-media-container`);

    this.productCardNode.appendChild(this.productCardMediaContainerNode);
  }

  /**
   * Determine if item should be a clickable anchor element or a div based on the presence of a product url
   * @param {String|null} url - product url, if there is no url it will return a div element instead of an anchor element
   * @param {String} variantId - variant id used for tracking and building url in case url is provided
   * @param {Boolean} [isText] - it's optional, set item as text
   * @returns {HTMLElement} - the created clickable item
   */
  #setClickableItem(url, variantId, isText = false) {
    let itemTag = url ? 'a' : 'div';
    if (!url && isText) {
      itemTag = 'span';
    }
    const item = document.createElement(itemTag);
    if (url) {
      item.href = this.#buildProductUrl(variantId);
      item.setAttribute('target', '_self');
    }

    return item;
  }

  /**
   * Build Product Card media (image and video if available)
   */
  #setProductCardMedia() {
    const productCardMediaWrapper = this.#setClickableItem(
      this.productCardData.productUrl,
      this.productCardData.variantId,
    );
    this.productCardMediaNode = productCardMediaWrapper;
    productCardMediaWrapper.classList.add('product-card-c-basis__images');
    // Set extra base class if provided
    this.extraBaseClassName && productCardMediaWrapper.classList.add(`${this.extraBaseClassName}__product-card-images`);

    // Injects first media element (image or video)
    const availableMedia = this.#getAvailableMedia(this.productCardData.variantId);
    // Injects first media element (video or image) whether it is available
    if (this.productCardData.videoSource && isVideo(availableMedia[0]).result) {
      productCardMediaWrapper.appendChild(
        this.#setProductCardVideo(availableMedia[0].split('_').at(-1), { hidden: true, position: 'first' }),
      );
    } else if (availableMedia[0]) {
      productCardMediaWrapper.appendChild(this.#setProductCardImage(availableMedia[0], 'first'));
    }

    // Injects second media element (video or image) whether it is available
    if (this.productCardData.videoSource && isVideo(availableMedia[1]).result) {
      productCardMediaWrapper.appendChild(
        this.#setProductCardVideo(availableMedia[1].split('_').at(-1), { hidden: true, position: 'second' }),
      );
    } else if (availableMedia[1]) {
      productCardMediaWrapper.appendChild(this.#setProductCardImage(availableMedia[1], 'second'));
    }
  }

  /**
   * Update product card images (not carousel) which only have two images
   * @param {String} variantId - variant id
   * @param {Object} selectedVariant - variant object that matches variant id
   */
  #updateProductCardMedia(variantId, selectedVariant) {
    const availableMedia = this.#getAvailableMedia(variantId);

    // Update first media element
    this.#updateMediaElement(availableMedia[0] || selectedVariant?.data?.image_url || '', 'first');
    // Update second media element
    this.#updateMediaElement(availableMedia[1], 'second');
    // Update URL
    this.productCardMediaNode.href = this.#buildProductUrl(variantId);
  }

  /**
   * Update media element in product card media node based on media source type and position (Only dekstop)
   * @param {String} mediaSrc - media source url
   * @param {String} position - position in the product card media node either first or second media element
   */
  #updateMediaElement(mediaSrc, position) {
    const element = this.productCardMediaNode.querySelector(`[data-${position}-element]`);

    if (!mediaSrc && !element) return; // do nothing

    if (!mediaSrc && element) {
      return element.remove(); // remove element if media source doesn't exist anymore
    }

    const isMediaVideo = isVideo(mediaSrc).result;
    const mediaCardDataByKey = position === 'first' ? 'imageUrl' : 'secondImageUrl';

    // if element doesn't exist, we just create it based on whether mediaSrc is a video or an image and exit the function
    if (!element && isMediaVideo) {
      return this.productCardMediaNode.appendChild(
        this.#setProductCardVideo(mediaSrc.split('_').at(-1), { hidden: true, position }),
      );
    }
    if (!element && !isMediaVideo) {
      this.productCardMediaNode.appendChild(this.#setProductCardImage(mediaSrc, position));
      return this.#setProductCardDataByKey(mediaCardDataByKey, mediaSrc);
    }

    // if element does exist, we proceed to evalue whether is a VIDEO or PICTURE
    // and also go over whether mediaSrc is a video or an image to update element properly
    if (element.tagName === 'PICTURE' && !isMediaVideo) {
      this.#updateImages(element, mediaSrc);
      this.#setProductCardDataByKey(mediaCardDataByKey, mediaSrc);
    } else if (element.tagName === 'PICTURE' && isMediaVideo) {
      // It might have been a image previously and now it is a video or it might be a video that needs to be updated
      element.remove();
      this.productCardMediaNode.appendChild(this.#setProductCardVideo(mediaSrc.split('_').at(-1), { position }));
    } else if (element.querySelector('video')?.tagName === 'VIDEO' && !isMediaVideo) {
      element.remove();
      this.productCardMediaNode.appendChild(this.#setProductCardImage(mediaSrc, position));
      this.#setProductCardDataByKey(mediaCardDataByKey, mediaSrc);
    } else if (element.querySelector('video')?.tagName === 'VIDEO' && isMediaVideo) {
      this.#updateVideo(element, mediaSrc.split('_').at(-1));
    }
  }

  /**
   * Build a consistent product card media carousel or gallery which comprises four images
   */
  #setProductCardMediaGallery() {
    const productCardMediaCarousel = document.createElement('div');
    productCardMediaCarousel.classList.add('product-card-c-gallery__scroll');
    this.productCardMediaNode = productCardMediaCarousel;

    const productCardMediaCarouselContainer = document.createElement('div');
    productCardMediaCarouselContainer.classList.add(
      'carousel-c-scroll__container',
      'product-card-c-gallery__container',
    );

    // Set its carousels items
    const availableMedia = this.#getAvailableMedia(this.productCardData.variantId);
    this.#appendMediaItems(productCardMediaCarouselContainer, availableMedia);

    productCardMediaCarousel.appendChild(productCardMediaCarouselContainer);

    const productCardMediaGalleryMarkers = document.createElement('ul');
    productCardMediaGalleryMarkers.classList.add('carousel-c-scroll__markers', 'product-card-c-gallery__markers');

    for (let i = 0; i < availableMedia.length; i++) {
      productCardMediaGalleryMarkers.appendChild(this.#buildMarker(i));
    }
    productCardMediaCarousel.appendChild(productCardMediaGalleryMarkers);

    this.#setMediaGallery();
  }

  /**
   * Update product card media carousel or gallery which contains a lot images
   * @param {String} variantId - variant id
   */
  #updateProductCardMediaGallery(variantId) {
    // Destroy events on current media carousel
    this.productCardMediaGalleryInstance.close();

    const productCardMediaCarouselContainer = this.productCardMediaNode.querySelector('.carousel-c-scroll__container');
    // Clean up carousel list items
    productCardMediaCarouselContainer.innerHTML = '';

    // Update carousel list items
    const availableMedia = this.#getAvailableMedia(variantId);
    this.#appendMediaItems(productCardMediaCarouselContainer, availableMedia);

    const productCardMediaCarouselMarkers = this.productCardMediaNode.querySelector('.carousel-c-scroll__markers');
    // Clean up marker list
    productCardMediaCarouselMarkers.innerHTML = '';

    for (let i = 0; i < availableMedia.length; i++) {
      productCardMediaCarouselMarkers.appendChild(this.#buildMarker(i));
    }

    this.#setMediaGallery();
  }

  /**
   * Append children media carousel items into carousel parent and based on set of available images
   * @param {HTMLUListElement} parentElement - UL parent container
   * @param {Array<String>} availableMedia
   * @returns
   */
  #appendMediaItems(parentElement, availableMedia) {
    return availableMedia.forEach((image, idx) => parentElement.appendChild(this.#buildMediaCarouselItem(image, idx)));
  }

  /**
   * Chooses the correct a fixed set of images that match variant id and style number
   * @param {String} variantId - variant id
   * @returns
   */
  #getAvailableMedia(variantId) {
    let availableMedia = this.productCardData.media.filter((image) =>
      image.includes(this.productCardData.options.find((option) => option.variantId === variantId)?.barcode),
    );

    // Let's choose the image that has variation assigned
    if (availableMedia.length === 0) {
      const assignedImage = this.productCardData.availableVariants.find((v) => v.data.variation_id === variantId)?.data
        ?.image_url;
      availableMedia = (assignedImage && [assignedImage]) || [];
    }

    // Look for a video that matches the variant's barcode and inject it in the correct position,
    // it applies in case the media is not already sorted by the backend.
    // video source can have multiple videos separated by | character.
    if (
      this.productCardData.videoSource &&
      this.productCardData.videoSource
        .split('|')
        .some((v) => v.includes(this.productCardData.options.find((option) => option.variantId === variantId)?.barcode))
    ) {
      const videos = this.productCardData.videoSource.split('|');
      // It will just choose one or more video that match the variant's barcode.
      const selectedVideos = videos.filter((v) =>
        v.includes(this.productCardData.options.find((option) => option.variantId === variantId)?.barcode),
      );

      // Iterate over selected videos and inject them in the correct position based on the naming convention of video files
      // that includes the position (e.g. video_0_barcode.mp4 for the first position, video_1_barcode.mp4 for the second position, etc.)
      selectedVideos.forEach((selectedVideo) => {
        const position = parseInt(selectedVideo.split('_')[1]);
        const videoId = selectedVideo.split('/').at(-1).split('.')[0];
        const existingVideoIdx = this.productCardData.media.findIndex((item) => item.includes(videoId));
        // Fall back case if video is not in the list of media, we won't add this video to the available media.
        if (existingVideoIdx < 0) return;

        // Insert the video element in the correct position based on the naming image convention
        // Furthermore, availableMedia array does not have videos previously.
        const setPosition = position === 0 && existingVideoIdx >= 0 ? 0 : position - 1;
        availableMedia.splice(setPosition, 0, selectedVideo);
      });
    }

    // We assume that the backend will send the media sorted by priority
    return availableMedia.slice(0, 4);
  }

  /**
   * Initialize product card media gallery
   */
  #setMediaGallery() {
    this.productCardMediaGalleryInstance = new ProductCardMediaGallery({
      gallery: this.productCardMediaNode,
      prefixClassName: 'carousel-c-scroll',
      gap: 0,
      extraToggleClass: 'product-card-c-gallery__marker--active',
    });

    this.productCardMediaGalleryInstance.init();
    this.productCardMediaGalleryInstance.resetPosition();
  }

  /**
   * Validate a string image url whether meets our own list of image extensions
   * @param {String} image - image url
   * @returns
   */
  #isImage(image) {
    return ProductCard.IMAGE_EXTENSIONS.some((ext) => image.includes(ext));
  }

  /**
   * Build a carousel media item
   * @param {String} image - image url
   * @param {Number} idx - position of the image in the array
   * @returns {HTMLAnchorElement} - carousel item
   */
  #buildMediaCarouselItem(image, idx) {
    const productCardMediaCarouselItem = this.#setClickableItem(
      this.productCardData.productUrl,
      this.productCardData.variantId,
    );
    productCardMediaCarouselItem.classList.add('carousel-c-scroll__list-item', 'product-card-c-gallery__list-item');
    productCardMediaCarouselItem.id = `carousel_${this.productCardData.productId}_card_${idx}`;

    // Set modifier
    productCardMediaCarouselItem.classList.add(
      `product-card-c-gallery__list-item--${this.#isImage(image) ? 'image' : 'video'}`,
    );

    // Set visible or hidden information inside of product card media carousel item
    if (idx >= 1) {
      this.#buildHiddenMediaItem(image, productCardMediaCarouselItem);
    } else {
      productCardMediaCarouselItem.appendChild(this.#buildVisibleMediaItem(image));
    }

    return productCardMediaCarouselItem;
  }

  /**
   * Set a visible product card media carousel child item
   * @param {String} image - image url
   * @returns {HTMLPictureElement | HTMLVideoElement } - HTML element either video or picture
   */
  #buildVisibleMediaItem(image) {
    if (this.#isImage(image)) {
      return this.#setProductCardImage(image);
    } else {
      // video element
      return this.#setProductCardVideo(image.split('_').at(-1), { hidden: true });
    }
  }

  /**
   * Set attributes or append a child element on product card media carousel item
   * @param {String} image - image url
   * @param {HTMLAnchorElement} element - product card media carousel item wrapper
   */
  #buildHiddenMediaItem(image, element) {
    if (this.#isImage(image)) {
      element.setAttribute('data-sizes', Object.values(imageBreakpoints).join(','));
      element.setAttribute('data-srcset', this.#buildMultiSrcSets(image));
      element.setAttribute('data-src', image);
      element.setAttribute('data-alt', this.productCardData.imageAlt);
      element.setAttribute('data-loading', 'eager');
      element.setAttribute('data-width', '200');
      element.setAttribute('data-height', '300');
    } else {
      // video element
      element.appendChild(this.#setProductCardVideo(image.split('_').at(-1), { hidden: true }));
    }
  }

  /**
   * Build a carousel marker item
   * @param {Number} i - number or index that belongs to an iteration
   * @returns {HTMLLIElement} - <li> element
   */
  #buildMarker(i) {
    const marker = document.createElement('li');
    marker.innerHTML = `
      <a href="#carousel_${
        this.productCardData.productId
      }_card_${i}" class="carousel-c-scroll__marker product-card-c-gallery__marker ${
        i == 0 ? 'carousel-c-scroll__marker--active product-card-c-gallery__marker--active' : ''
      }">`;
    return marker;
  }

  /**
   * Bind events for Product Card Media element
   * @returns {void} - there is no return
   */
  #bindProductCardEvents() {
    // Prevent hover events on mobile devices
    if (window.innerWidth < ProductCard.TABLET_BREAKPOINT) return;

    this.productCardMediaNode.addEventListener('mouseenter', () => {
      const firstElement = this.productCardMediaNode.querySelector('[data-first-element]');
      const secondElement = this.productCardMediaNode.querySelector('[data-second-element]');
      if (firstElement && secondElement) {
        firstElement.style.opacity = '0';

        const firstVideoElement = firstElement.querySelector('video');
        const secondVideoElement = secondElement.querySelector('video');
        if (secondVideoElement?.tagName === 'VIDEO') {
          secondVideoElement.querySelectorAll('source').forEach((source) => {
            if (!source.src) {
              source.src = applyVideoFetchPrefix(source.dataset.src);
            }
          });
          !secondVideoElement.hasAttribute('data-loaded') && secondVideoElement.load();
          secondVideoElement.setAttribute('data-loaded', true);
          secondVideoElement.play();
        }
        if (firstVideoElement?.tagName === 'VIDEO') {
          firstVideoElement.pause();
        }
      }
    });

    this.productCardMediaNode.addEventListener('mouseleave', () => {
      const firstElement = this.productCardMediaNode.querySelector('[data-first-element]');
      const secondElement = this.productCardMediaNode.querySelector('[data-second-element]');
      if (firstElement && secondElement) {
        firstElement.style.opacity = '1';

        const firstVideoElement = firstElement.querySelector('video');
        const secondVideoElement = secondElement.querySelector('video');
        secondVideoElement?.tagName === 'VIDEO' && secondVideoElement.pause();
        firstVideoElement?.tagName === 'VIDEO' && firstVideoElement.play();
      }
    });

    // Inject an intersection Observer only if there is a video in the first position.
    const hasVideoAsTheFirstPos = this.productCardMediaNode.querySelector('div')?.hasAttribute('data-first-element');

    if (!hasVideoAsTheFirstPos) return;

    const firstVideoObserverCallback = generateVideoObserverCallback(true);
    const firstVideoObserverOptions = generateVideoObserverOptions(null);
    // Set observers only on the first video element
    const observer = new IntersectionObserver(firstVideoObserverCallback, firstVideoObserverOptions);
    observer.observe(this.productCardMediaNode);
  }

  /**
   * Bind click tracking events for product media and title
   */
  #bindTrackingEvents() {
    // Capture-phase so the Constructor beacon dispatches before the anchor navigates away
    this.productCardMediaNode.addEventListener('click', this.productClickTracking.bind(this), true);
    this.productCardTitleLinkNode.addEventListener('click', this.productClickTracking.bind(this), true);
  }

  /**
   * Build Product Card image and set a first image
   * @returns {HTMLPictureElement} - picture element
   */
  #setProductCardImage(imageUrl, position = '') {
    // Create product card image element
    const productCardImage = document.createElement('picture');
    position && productCardImage.setAttribute(`data-${position}-element`, '');

    const imgElement = document.createElement('img');
    const isSecondImage = position === 'second';
    imgElement.loading =
      !isSecondImage && this.productCardData.imageLoading ? this.productCardData.imageLoading : 'lazy';
    if (!isSecondImage && this.productCardData.imageFetchPriority) {
      imgElement.fetchPriority = this.productCardData.imageFetchPriority;
    }
    imgElement.src = imageUrl;
    imgElement.alt = this.productCardData.imageAlt;
    imgElement.width = 200;
    imgElement.height = 300;

    // Handle image load errors (no network request fallback)
    imgElement.onerror = imageErrorHandler;

    for (const screenSize of Object.keys(imageSizes)) {
      productCardImage.appendChild(this.#buildSource(imageUrl, screenSize));
    }

    productCardImage.appendChild(imgElement);

    return productCardImage;
  }

  /**
   * Build a consistent source HTML element
   * @param {String} image - image url
   * @param {String} layout - type of layout either mobile, desktop or widescreen
   * @returns {HTMLSourceElement} - a source HTML element
   */
  #buildSource(image, layout) {
    return buildSource({ image, layout, setHeight: true });
  }

  #setProductCardImageHidden() {
    const productCardMediaWrapper = this.#setClickableItem(
      this.productCardData.productUrl,
      this.productCardData.variantId,
    );
    this.productCardMediaNode = productCardMediaWrapper;
    productCardMediaWrapper.classList.add('product-card-c-basis__images');
    // Set extra base class if provided
    this.extraBaseClassName && productCardMediaWrapper.classList.add(`${this.extraBaseClassName}__product-card-images`);

    productCardMediaWrapper.setAttribute('data-sizes', Object.values(imageBreakpoints).join(','));
    productCardMediaWrapper.setAttribute('data-srcset', this.#buildMultiSrcSets(this.productCardData.imageUrl));
    productCardMediaWrapper.setAttribute('data-src', this.productCardData.imageUrl);
    productCardMediaWrapper.setAttribute('data-alt', this.productCardData.imageAlt);
    productCardMediaWrapper.setAttribute('data-loading', 'eager');

    if (this.productCardData.videoSource) {
      productCardMediaWrapper.setAttribute('data-second-media-type', 'video');
      productCardMediaWrapper.setAttribute('data-second-media-src', this.productCardData.videoSource);
    } else if (this.productCardData.secondImageUrl) {
      productCardMediaWrapper.setAttribute('data-second-media-type', 'image');
      productCardMediaWrapper.setAttribute('data-second-media-sizes', Object.values(imageBreakpoints).join(','));
      productCardMediaWrapper.setAttribute(
        'data-second-media-srcset',
        this.#buildMultiSrcSets(this.productCardData.secondImageUrl),
      );
      productCardMediaWrapper.setAttribute('data-second-media-src', this.productCardData.secondImageUrl);
      productCardMediaWrapper.setAttribute('data-second-media-alt', this.productCardData.imageAlt);
      productCardMediaWrapper.setAttribute('data-second-media-loading', 'eager');
    }
  }

  /**
   * Build a string with multiple set of sources or multiple srcsets
   * @param {String} image - a image url
   * @returns {String} - multiple source sets separated by |
   */
  #buildMultiSrcSets(image) {
    return Object.keys(imageSizes)
      .reduce((srcSets, layout) => {
        srcSets.push(buildSrcSet({ image, layout, setHeight: true }));
        return srcSets;
      }, [])
      .join('|');
  }

  /**
   * Build Product Card video element
   * @param {String} videoUrl - video url
   * @param {{}} [context={}] - Context or feature flags
   * @returns {HTMLVideoElement} - video element
   */
  #setProductCardVideo(videoUrl, context = {}) {
    return buildVideoElement(videoUrl, this.productCardData.imageAlt, { placement: 'tile', ...context });
  }

  /**
   * Set as many color swatches as they are posible, sellable, and available
   */
  #setColorSwatches() {
    if (!this.productCardData.colorSwatches) return;

    const productCardColorSwatchesWrapper = document.createElement('ul');
    productCardColorSwatchesWrapper.classList.add('product-card-c-basis__swatches-wrapper');
    // Set extra base class if provided
    this.extraBaseClassName &&
      productCardColorSwatchesWrapper.classList.add(`${this.extraBaseClassName}__product-card-swatches-wrapper`);

    if (this.productCardData.options?.length) {
      // Build all swatches up to desktop limit
      this.productCardData.options.slice(0, ProductCard.DESKTOP_SWATCH_LIMIT).forEach((option, index) => {
        productCardColorSwatchesWrapper.appendChild(this.#buildColorSwatch(option, index));
      });

      // Always create count element for dynamic updates
      const restOfColors = document.createElement('span');
      restOfColors.classList.add('product-card-c-basis__swatches-count');
      // Set extra base class if provided
      this.extraBaseClassName && restOfColors.classList.add(`${this.extraBaseClassName}__product-card-swatches-count`);
      this.swatchCountNode = restOfColors;
      productCardColorSwatchesWrapper.appendChild(restOfColors);
    }

    // Store reference for use in switchColor
    this.productCardColorSwatchesWrapperNode = productCardColorSwatchesWrapper;

    this.productCardNode.appendChild(productCardColorSwatchesWrapper);
  }

  /**
   * Update swatch visibility based on viewport width
   * Shows 4 swatches on mobile (<768px) and 5 on desktop
   */
  #updateSwatchVisibility() {
    const isMobile = window.innerWidth < ProductCard.MOBILE_BREAKPOINT;
    const limit = isMobile ? ProductCard.MOBILE_SWATCH_LIMIT : ProductCard.DESKTOP_SWATCH_LIMIT;
    const totalOptions = this.productCardData.options?.length || 0;

    // Toggle visibility of 5th swatch (index 4)
    const fifthSwatch = this.productCardColorSwatchesWrapperNode?.querySelector('[data-swatch-index="4"]');
    if (fifthSwatch) {
      fifthSwatch.style.display = isMobile ? 'none' : '';
    }

    // Update count element
    const remaining = totalOptions - limit;
    if (this.swatchCountNode) {
      this.swatchCountNode.style.display = remaining > 0 ? '' : 'none';
      this.swatchCountNode.innerText = remaining > 0 ? `+${remaining}` : '';
    }
  }

  /**
   * Switch media node among hover images and gallery carousel and viceversa
   * @returns {void} - nothing
   */
  #updateMediaVisibility() {
    const galleryNode = this.productCardMediaContainerNode.querySelector('.product-card-c-gallery__scroll');
    const imagesNode = this.productCardMediaContainerNode.querySelector('.product-card-c-basis__images');
    const isGalleryHidden = galleryNode?.classList.contains('is-hidden');
    const isImagesHidden = imagesNode?.classList.contains('is-hidden');

    // Guard condition when these nodes were already rendered
    if (galleryNode && imagesNode && window.innerWidth < ProductCard.TABLET_BREAKPOINT) {
      if (isImagesHidden && !isGalleryHidden) {
        return;
      }
    } else {
      if (!isImagesHidden && isGalleryHidden) {
        return;
      }
    }

    if (window.innerWidth < ProductCard.TABLET_BREAKPOINT) {
      if (galleryNode && imagesNode && isGalleryHidden) {
        galleryNode.classList.toggle('is-hidden');
        imagesNode.classList.toggle('is-hidden');
        this.productCardMediaNode = galleryNode;
        return;
      }

      if (!galleryNode) {
        this.productCardMediaNode.classList.toggle('is-hidden');
        // This will set media gallery and set it as the new media node
        this.#setProductCardMediaGallery();
        // Brings gallery to front
        this.productCardMediaContainerNode.prepend(this.productCardMediaNode);
      }

      return;
    } else {
      if (imagesNode && galleryNode && isImagesHidden) {
        galleryNode.classList.toggle('is-hidden');
        imagesNode.classList.toggle('is-hidden');
        this.productCardMediaNode = imagesNode;
      }

      if (!imagesNode) {
        this.productCardMediaNode.classList.toggle('is-hidden');
        // This will set images and set it as the new current media node
        this.#setProductCardMedia();
        // Bring images to front
        this.productCardMediaContainerNode.prepend(this.productCardMediaNode);
        this.#bindProductCardEvents();
      }

      return;
    }
  }

  /**
   * Setup ResizeObserver to handle responsive swatch visibility
   * Also handles edge case when 5th swatch is selected and viewport changes to mobile
   */
  #setupSwatchResizeObserver() {
    this.swatchResizeObserver = new ResizeObserver(() => {
      this.#updateSwatchVisibility();
      this.productCardData.mediaSwitch && this.#updateMediaVisibility();

      // Handle edge case: 5th swatch selected on mobile - auto-switch to 1st
      const isMobile = window.innerWidth < ProductCard.MOBILE_BREAKPOINT;
      if (isMobile) {
        const activeSwatch = this.productCardColorSwatchesWrapperNode?.querySelector('.is-active');
        const activeIndex = parseInt(activeSwatch?.dataset.swatchIndex, 10);
        if (activeIndex >= ProductCard.MOBILE_SWATCH_LIMIT) {
          const firstSwatch = this.productCardColorSwatchesWrapperNode?.querySelector('[data-swatch-index="0"]');
          firstSwatch?.click();
        }
      }
    });

    this.swatchResizeObserver.observe(this.productCardNode);
  }

  /**
   * Set an event on a specific color swatch. This will change image and different urls
   * employed inside of Product Card
   * @param {MouseEvent} event
   * @returns - There is no return
   */
  #switchColor(event) {
    event.stopImmediatePropagation();
    event.preventDefault();

    if (!event) return;

    const currentTarget = event.currentTarget;

    // If the clicked swatch is already active, do nothing
    if (currentTarget.classList.contains('is-active')) return;

    const parentNode = event.currentTarget.closest('ul');
    const swatches = parentNode.querySelectorAll('.product-card-c-basis__swatch-item');

    // Remove active and secondary classes from all swatches
    swatches.forEach((child) => {
      child.classList.remove('is-active', 'secondary');
    });

    // Set clicked swatch as active
    currentTarget.classList.add('is-active');

    // Get selected variant data
    const variantId = currentTarget.dataset.variantId;
    this.productCardData.variantId = variantId;
    const selectedVariant = this.productCardData.availableVariants?.find((v) => v.data.variation_id === variantId);

    // Update images - filter by barcode, fallback to variant's image_url for gift cards
    if (this.productCardData.isCardImageGallery && window.innerWidth < ProductCard.TABLET_BREAKPOINT) {
      this.#updateProductCardMediaGallery(variantId);
    } else {
      this.#updateProductCardMedia(variantId, selectedVariant);
    }

    // Update price if variant data is available
    if (selectedVariant) {
      this.#updatePrice(selectedVariant);
    }

    setTimeout(() => {
      // Update remaining URLs
      this.productCardTitleLinkNode.href = this.#buildProductUrl(variantId);
      // Update Constructor tracking attributes for the new variant
      this.#updateConstructorAttributes(variantId, selectedVariant);
      // Update QuickAdd with new variant
      if (this.quickAddInstance) {
        this.quickAddInstance.updateForVariant(variantId);
      }
    }, 0);
  }

  /**
   * Update Constructor tracking attributes when variant changes
   * @param {String} variantId - the new variant ID
   * @param {Object} variant - the selected variant object
   */
  #updateConstructorAttributes(variantId, variant) {
    if (!this.productCardNode) return;

    // Update variation ID
    this.productCardNode.setAttribute('data-cnstrc-item-variation-id', variantId || '');

    // Update price if variant data is available
    if (variant?.data) {
      const displayPrice = this.#calculateConstructorDisplayPrice(variant.data);
      if (displayPrice) {
        this.productCardNode.setAttribute('data-cnstrc-item-price', displayPrice);
      }
    }
  }

  /**
   * Calculate display price for Constructor tracking (min of sale/retail price)
   * @param {Object} variantData - variant data object
   * @returns {string} - Numeric price value as string
   */
  #calculateConstructorDisplayPrice(variantData) {
    const price = getConstructorPrice(variantData);
    return price ? price.toString() : '';
  }

  #updateImages(element, imageUrl) {
    element.querySelectorAll('source').forEach((source, idx) => {
      source.srcset = buildSrcSet({ image: imageUrl, layout: Object.keys(imageBreakpoints)[idx], setHeight: true });
    });
    const img = element.querySelector('img');
    if (img) {
      img.src = imageUrl;
    }
  }

  /**
   * Update video element with new video URL
   * @param {HTMLVideoElement} element - HTML video element
   * @param {String} videoUrl - CDN video url
   */
  #updateVideo(element, videoUrl) {
    const video = element.querySelector('video');
    const source = video?.querySelector('source');
    if (video && source) {
      const placement = source.dataset.placement || 'tile';
      source.src = applyVideoFetchPrefix(videoUrl, placement);
      video.load();
    }
  }

  #setProductCardDataByKey(key, value) {
    this.productCardData[key] = value;
  }

  #buildProductUrl(variantId) {
    if (!this.productCardData.productUrl && !variantId) return '';

    // Placeholder products already have the real variant ID baked into their URL
    if (this.productCardData.isPlaceholder) {
      return this.productCardData.productUrl;
    }

    if (!variantId) return this.productCardData.productUrl;

    return `${this.productCardData.productUrl}${
      this.productCardData.productUrl.includes('?') ? '&' : '?'
    }variant=${variantId}`;
  }

  /**
   * Update price display when variant changes
   * @param {Object} variant - variant object from Constructor API
   */
  #updatePrice(variant) {
    // Get country-specific price fields
    const country = window.Shopify?.country || 'US';
    const priceField = country !== 'US' ? `price${country}` : 'priceUS';
    const compareAtPriceField = country !== 'US' ? `compareAtPrice${country}` : 'compareAtPriceUS';

    const regularPrice = variant.data[priceField] ?? variant.data.priceUS;
    const compareAtPrice = variant.data[compareAtPriceField] ?? variant.data.compareAtPriceUS;

    if (!regularPrice) return;

    // Update productCardData
    this.productCardData.price = regularPrice;
    this.productCardData.compareAtPrice = compareAtPrice;

    // Re-render price using existing setPrice method
    this.#renderPrice(this.productCardPriceNode);
  }

  /**
   * Build Quick Add experience
   */
  #setQuickAdd() {
    const quickAddInstance = new QuickAdd({
      variants: this.productCardData.variants,
      options: this.productCardData.options,
      productUrl: this.productCardData.productUrl,
      productId: this.productCardData.productId,
      title: this.productCardData.title,
      location: this.productCardData.location,
    });
    this.quickAddInstance = quickAddInstance;
    // Append to media container (wrapper for images + QuickAdd)
    this.productCardMediaContainerNode.appendChild(quickAddInstance.node);
  }

  /**
   * Inject a blank box element only for specific products such as GIVEX products
   * This will fill out the space where Quick Add button was not rendered and
   * allow aligning these product cards with the rest of ones that do have Quick Add button
   * Only helpful for mobile layout, not for desktop devices.
   */
  #setBlankBox() {
    const blankBox = document.createElement('div');
    blankBox.classList.add('product-card-c-basis__blank-box');
    this.productCardMediaContainerNode.appendChild(blankBox);
  }

  /**
   * Build the Product Card title link
   */
  #setTitle() {
    const productCardTitleLink = this.#setClickableItem(
      this.productCardData.productUrl,
      this.productCardData.variantId,
      true,
    );
    this.productCardTitleLinkNode = productCardTitleLink;
    productCardTitleLink.classList.add('product-card-c-basis__title');
    // Set extra base class if provided
    this.extraBaseClassName && productCardTitleLink.classList.add(`${this.extraBaseClassName}__product-card-title`);
    productCardTitleLink.tabIndex = 0;

    productCardTitleLink.innerText = this.productCardData.title;

    this.productCardNode.appendChild(productCardTitleLink);
  }

  /**
   * Build the Product Card footer which includes prices, promo messaging and reviews
   */
  #setFooter() {
    const productCardInfoPod = document.createElement('div');
    productCardInfoPod.classList.add('product-card-c-basis__footer-pod');
    // Set extra base class if provided
    this.extraBaseClassName && productCardInfoPod.classList.add(`${this.extraBaseClassName}__product-card-footer-pod`);
    this.productCardFooterPodNode = productCardInfoPod;
    this.productCardNode.appendChild(this.productCardFooterPodNode);
  }

  /**
   * Build ProductCard prices based on price, compare_at_price, and currency
   */
  #setPrice() {
    let priceWrapper = this.productCardNode.querySelector('.product-card-c-basis__prices');

    // If price wrapper doesn't exist, create it
    if (!priceWrapper) {
      priceWrapper = document.createElement('div');
      priceWrapper.classList.add('product-card-c-basis__prices');
      // Set extra base class if provided
      this.extraBaseClassName && priceWrapper.classList.add(`${this.extraBaseClassName}__product-card-prices`);
      this.productCardPriceNode = priceWrapper;
      this.productCardFooterPodNode.appendChild(priceWrapper);
    }

    // Render price content
    this.#renderPrice(priceWrapper);
  }

  /**
   * Format price with currency symbol at the beginning
   * @param {Number} price - the price to format
   * @returns {String} - formatted price string
   */
  #formatPrice(price) {
    // Shared with the announcement-bar threshold formatter so both surfaces render
    // currency identically (see src/scripts/utils/formatPriceWithSymbol.js).
    return formatPriceWithSymbol(price);
  }

  /**
   * Render price HTML into the price wrapper element
   * @param {HTMLElement} priceWrapper - the price wrapper element
   */
  #renderPrice(priceWrapper) {
    // If we have price data, display it
    if (this.productCardData.price) {
      const compareAtPrice = this.productCardData.compareAtPrice;
      const regularPrice = this.productCardData.price;

      // Calculate min and max prices from variants if available
      let minPrice = regularPrice;
      let maxPrice = regularPrice;

      if (this.productCardData.availableVariants && this.productCardData.availableVariants.length > 0) {
        // Get country-specific price field for variant price range calculation
        const country = window.Shopify?.country || 'US';
        const priceField = country !== 'US' ? `price${country}` : 'priceUS';

        const prices = this.productCardData.availableVariants
          .map((v) => v.data?.[priceField] ?? v.data?.priceUS)
          .filter((p) => p != null);

        if (prices.length > 0) {
          minPrice = Math.min(...prices);
          maxPrice = Math.max(...prices);
        }
      }

      // Check if product has price range (variants with different prices)
      const hasPriceRange = minPrice !== maxPrice;

      // Format prices with currency symbol at the beginning
      const formattedMinPrice = this.#formatPrice(minPrice);
      const formattedMaxPrice = this.#formatPrice(maxPrice);

      // Product has price range - show range format - exclusive to 'givex' product type
      if (hasPriceRange && isProductType(this.productCardData.productType, 'givex')) {
        priceWrapper.setAttribute('aria-label', `Price: ${formattedMinPrice} - ${formattedMaxPrice}`);
        priceWrapper.innerHTML = `
          <span class="product-card-c-basis__prices--regular ${
            this.extraBaseClassName ? `${this.extraBaseClassName}__product-card-prices--regular` : ''
          }">${formattedMinPrice} - ${formattedMaxPrice}</span>
        `;
      }
      // Single price with compare-at price (on sale)
      else if (compareAtPrice && compareAtPrice > regularPrice) {
        // Calculate discount percentage
        const discountPercentage = Math.round(((compareAtPrice - regularPrice) / compareAtPrice) * 100);
        const formattedComparePrice = this.#formatPrice(compareAtPrice);
        const formattedRegularPrice = this.#formatPrice(regularPrice);

        priceWrapper.setAttribute(
          'aria-label',
          `Price: ${formattedRegularPrice}, Before: ${formattedComparePrice}, Discount: ${discountPercentage}% off`,
        );
        priceWrapper.innerHTML = `
          <span class="product-card-c-basis__prices--sale ${
            this.extraBaseClassName ? `${this.extraBaseClassName}__product-card-prices--sale` : ''
          }">${formattedRegularPrice}</span>
          <span class="product-card-c-basis__prices--compare ${
            this.extraBaseClassName ? `${this.extraBaseClassName}__product-card-prices--compare` : ''
          }">${formattedComparePrice}</span>
          <span class="product-card-c-basis__prices--discount ${
            this.extraBaseClassName ? `${this.extraBaseClassName}__product-card-prices--discount` : ''
          }">${discountPercentage}% off</span>
        `;
      }
      // Single price (full price, no sale)
      else {
        const formattedRegularPrice = this.#formatPrice(regularPrice);
        priceWrapper.setAttribute('aria-label', `Price: ${formattedRegularPrice}`);
        priceWrapper.innerHTML = `
          <span class="product-card-c-basis__prices--regular ${
            this.extraBaseClassName ? `${this.extraBaseClassName}__product-card-prices--regular` : ''
          }">${formattedRegularPrice}</span>
        `;
      }
    }
  }

  /**
   * Create a new Badge on ProductCard
   */
  #setBadge() {
    if (!this.productCardData.isBadgeVisible || !this.productCardData.badge?.text) return;

    this.#buildBadgeNode();
    this.#setRegularBadge();

    this.productCardData.badge?.text.toLowerCase() === 'sale' &&
      this.productCardBadgeNode.setAttribute('data-sale-badge', 'true');
    return;
  }

  /**
   * Introduce Badge Node in the DOM
   */
  #buildBadgeNode() {
    const badge = document.createElement('div');
    this.productCardBadgeNode = badge;
    badge.classList.add('product-card-c-basis__badge');
    // Set extra base class if provided
    this.extraBaseClassName && badge.classList.add(`${this.extraBaseClassName}__product-card-badge`);
    this.productCardNode.appendChild(badge);
  }

  /**
   * Create a Ordinary or regular Badge based on ProductCard Badge property
   * @returns {void}
   */
  #setRegularBadge() {
    // Create a text badge
    if (!this.productCardData.badge?.imageUrl) {
      this.productCardBadgeNode.classList.add('product-card-c-basis__badge--text');
      // Set extra base class if provided
      this.extraBaseClassName &&
        this.productCardBadgeNode.classList.add(`${this.extraBaseClassName}__product-card-badge--text`);
      this.productCardBadgeNode.textContent = this.productCardData.badge?.text || '';
      return;
    }

    this.productCardBadgeNode.classList.add('product-card-c-basis__badge--image');
    // Set extra base class if provided
    this.extraBaseClassName &&
      this.productCardBadgeNode.classList.add(`${this.extraBaseClassName}__product-card-badge--image`);
    const img = document.createElement('img');
    img.src = this.productCardData.badge.imageUrl;
    img.alt = this.productCardData.badge.text || '';
    img.height = 22;
    this.productCardBadgeNode.appendChild(img);

    return;
  }

  /**
   * Build a new url adding new query parameters based on variant id value
   * @param {String} variantId - product variant id value
   * @returns {String} - new url
   */
  buildUrlOption(variantId) {
    const baseUrl = this.productCardData.productUrl.split('?')[0];
    const rectToken = this.productCardData.productUrl.split('recToken=')[1];
    return `${baseUrl}?variant=${variantId}&recToken=${rectToken}`;
  }

  /**
   * Build just one color swatch instance and set up its color
   * @param {Object} option - Product Card object item
   * @param {Number} index - color swatch's position
   * @returns Color Swatch instance
   */
  #buildColorSwatch(option, index) {
    const productCardColorSwatchLinkPod = document.createElement('li');
    const productCardColorSwatchLink = document.createElement('a');
    const productCardColorSwatchImage = document.createElement('img');

    productCardColorSwatchLink.classList.add('product-card-c-basis__swatch-item', 'avail');
    // Set extra base class if provided
    this.extraBaseClassName &&
      productCardColorSwatchLink.classList.add(`${this.extraBaseClassName}__product-card-swatch-item`);

    // Detect gift cards by product type (GIVEX) or barcode pattern (starts with GIFTCARD)
    const swatch = isProductType(this.productCardData.productType, 'givex') ? 'GCS' : 'SWT';

    const bgUrl = `https://cdn.shopify.com/s/files/1/0070/8853/7651/t/8/assets/${option.barcode}_${swatch}_48x48_crop_center.jpg?v=${window.theme.swatches_updated_at}`;

    // Sanitize color name for use as CSS class - replace ALL spaces and special chars
    // Apart from that, this optional chaining operator (?.) will allow us to avoid errors and will render a empty color-swatch
    const sanitizedColor = option.color?.replace(/\s+/g, '_').toLowerCase().split('-').at(0);

    productCardColorSwatchImage.setAttribute('data-fallback', `isp-product-color-swatch-${sanitizedColor}`);
    productCardColorSwatchImage.onerror = function () {
      this.style.display = 'none';
      this.parentNode.classList.add(this.dataset.fallback);
    };
    productCardColorSwatchImage.src = bgUrl;
    productCardColorSwatchImage.width = 48;
    productCardColorSwatchImage.height = 48;
    productCardColorSwatchImage.alt = `Color swatch ${option.color}`;
    // Above-the-fold priority applies to the card's primary image, not every swatch.
    productCardColorSwatchImage.loading = 'lazy';

    // Add data-index for CSS ordering (used when collapsed to show active first)
    productCardColorSwatchLink.dataset.swatchIndex = index;

    if (index === 0) {
      productCardColorSwatchLink.classList.add('is-active');
    }

    productCardColorSwatchLink.setAttribute('data-variant-id', option.variantId);
    productCardColorSwatchLink.title = option.color;
    productCardColorSwatchLink.role = 'button';
    productCardColorSwatchLink.setAttribute('aria-label', `Color: ${option.color}`);
    if (this.productCardData.isCardImageHidden) {
      productCardColorSwatchLink.setAttribute('data-background-image', bgUrl);
    }

    productCardColorSwatchLink.tabIndex = 0;

    productCardColorSwatchLink.addEventListener('click', this.#switchColor.bind(this));
    productCardColorSwatchLink.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#switchColor(e);
      }
    });

    productCardColorSwatchLink.appendChild(productCardColorSwatchImage);
    productCardColorSwatchLinkPod.appendChild(productCardColorSwatchLink);
    return productCardColorSwatchLinkPod;
  }

  /**
   * Build and render a promo message
   */
  #setPromoMessage() {
    const promoMessagePod = document.createElement('div');
    promoMessagePod.classList.add('product-card-c-basis__promo-message');
    // Set extra base class if provided
    this.extraBaseClassName && promoMessagePod.classList.add(`${this.extraBaseClassName}__product-card-promo-message`);
    promoMessagePod.innerHTML = this.productCardData.promoMessage;
    this.productCardFooterPodNode.appendChild(promoMessagePod);
  }

  /**
   * Build Product Card Reviews
   */
  #setReviews() {
    if (!this.productCardData.reviews?.average) return;
    const reviewsInstance = new ProductReviews(this.productCardData.reviews, this.extraBaseClassName);
    this.productCardFooterPodNode.appendChild(reviewsInstance.reviewsNode);
  }

  /**
   * Set the product click tracking for AdMind, Google Analytics, and Constructor.io
   */
  productClickTracking() {
    if (!this.productCardNode) return;

    // Get collection name for tracking
    let collectionName = this.productCardNode.closest('[data-collection]');
    let listName = '';

    if (collectionName) {
      listName = collectionName.dataset.collection;
    } else {
      listName = window.theme.collection || window.theme.collectionName || 'Product Carousel';
    }

    if (listName) {
      sessionStorage.setItem('rec_list_name', listName);
    }

    // Fire promotion click event if this product is part of a promo carousel
    if (this.productCardData.promoCreative && this.productCardData.trackingId) {
      const dataLayer = window.dataLayer || [];
      // Get the promotion data from the element's tracking attributes
      const promoData = {
        id: this.productCardData.trackingId,
        creative: this.productCardData.promoCreative,
        name: this.productCardData.title,
        position: this.productCardData.index + 1,
      };

      // Push select_promotion event for GA4 / promoClick for Enhanced Ecommerce
      dataLayer.push({
        event: 'select_promotion',
        ecommerce: {
          items: [],
        },
        ...promoData,
      });
    }

    // Calculate max compare price from variants
    const comparePrices = this.productCardData.variants.map((variant) => variant.compare_at_price || 0);
    const maxComparePrice = Math.max(...comparePrices);

    // Prepare product data for AdMind tracking
    const product = {
      pid: this.productCardData.productId,
      title: this.productCardData.title,
      product_type: this.productCardData.productType,
      price: this.productCardData.price,
      price_compare: this.productCardData.compareAtPrice || 0,
      price_compare_max: maxComparePrice,
      tags: this.productCardData.tags || [],
      variants: this.productCardData.variants,
      list_name: listName,
      product_url: this.#buildProductUrl(this.productCardData.variantId),
      position: this.productCardData.index + 1,
      reviewsCount: this.productCardData.reviews?.count || 0,
      review: this.productCardData.reviews?.average || 0,
      podId: this.productCardData.podId || '',
    };

    // Call AdMind click tracking
    AdMindClick(product);

    // Constructor click tracking
    const browseContainer = this.productCardNode.closest('[data-cnstrc-browse]');
    const searchContainer = this.productCardNode.closest('[data-cnstrc-search]');
    const autosuggestItem = this.productCardNode.getAttribute('data-cnstrc-item') === 'autosuggest';
    const resultId = this.productCardNode.closest('[data-cnstrc-result-id]')?.dataset.cnstrcResultId || '';
    const itemId = this.productCardNode.getAttribute('data-cnstrc-item-id');
    const itemName = this.productCardNode.getAttribute('data-cnstrc-item-name');
    const variationId = this.productCardNode.getAttribute('data-cnstrc-item-variation-id');

    if (searchContainer || autosuggestItem) {
      const term =
        new URLSearchParams(window.location.search).get('q') ||
        document.querySelector('[data-cnstrc-search-input]')?.value ||
        '';
      trackSearchResultClick(term, { itemName, itemId, variationId, resultId });
    } else if (browseContainer) {
      trackBrowseResultClick({
        filterName: 'group_id',
        filterValue: browseContainer.closest('[data-collection-id]')?.dataset.collectionId || '',
        itemId,
        itemName,
        variationId,
        resultId,
      });
    }
  }

  /**
   * Cleanup resources when the ProductCard is destroyed
   */
  destroy() {
    if (this.swatchResizeObserver) {
      this.swatchResizeObserver.disconnect();
      this.swatchResizeObserver = null;
    }
  }
}
